import React from 'react';
import { Trash2, ChevronDown, Plus, Info, GripVertical } from 'lucide-react';
import { makeId } from '../CreateInterviewModal';
import { useDropdown } from '../../hooks/useOutsideClick';

const TIMES = [1, 2, 3, 4];
const MAX_QUESTIONS = 20;

function TimerDropdown({ value, onChange }) {
  const { open, toggle, close, ref } = useDropdown();
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#5B4FE8] hover:underline"
      >
        {value} min
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 top-7 z-20 bg-white border border-[#ECE6FA] rounded-xl shadow-lg py-1 min-w-[90px]">
          {TIMES.map((t) => (
            <button
              key={t}
              onClick={() => { onChange(t); close(); }}
              className={`w-full px-4 py-2 text-left text-[13px] hover:bg-[#F1ECFE] ${value === t ? 'text-[#5B4FE8] font-semibold' : 'text-[#1A1533]'}`}
            >
              {t} min
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function QuestionRow({ question, index, onUpdate, onRemove }) {
  return (
    <div className="flex items-stretch gap-2">
      <div className="flex items-center px-1 text-[#8A83A5]">
        <GripVertical size={14} />
      </div>
      <div className="flex-1 rounded-xl border border-[#ECE6FA] bg-[#FAF8FF] p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13.5px] font-semibold text-[#1A1533]">Question {index + 1}</span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-[12.5px] text-[#4A4463]">
              Set Timer:
              <TimerDropdown value={question.time} onChange={(t) => onUpdate({ time: t })} />
            </div>
            <button
              onClick={onRemove}
              className="w-7 h-7 rounded-md flex items-center justify-center text-[#DC3545] hover:bg-[#FDECEC]"
              aria-label="Remove question"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        <textarea
          value={question.q}
          onChange={(e) => onUpdate({ q: e.target.value })}
          placeholder="Write question here"
          rows={2}
          className="w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white outline-none text-[13.5px] placeholder-[#9A93B0] focus:ring-2 focus:ring-[#5B4FE8]/30 resize-none"
        />

        <div className="mt-3">
          <div className="flex items-center gap-1 text-[12.5px] font-semibold text-[#1A1533] mb-1.5">
            Ideal Answer <span className="text-[#8A83A5] font-normal">(Optional)</span>
            <Info size={11} className="text-[#A39DB8]" />
          </div>
          <textarea
            value={question.ideal}
            onChange={(e) => onUpdate({ ideal: e.target.value })}
            placeholder="Define expected answer"
            rows={2}
            className="w-full px-3 py-2.5 rounded-lg border border-[#ECE6FA] bg-white outline-none text-[13px] placeholder-[#9A93B0] focus:ring-2 focus:ring-[#5B4FE8]/30 resize-none"
          />
        </div>
      </div>
    </div>
  );
}

export default function CustomQuestionsPanel({ module, onChange }) {
  const questions = module.questions || [];

  const update = (id, patch) => {
    onChange({ questions: questions.map((q) => (q.id === id ? { ...q, ...patch } : q)) });
  };
  const remove = (id) => {
    onChange({ questions: questions.filter((q) => q.id !== id) });
  };
  const add = () => {
    if (questions.length >= MAX_QUESTIONS) return;
    onChange({ questions: [...questions, { id: makeId('q'), q: '', ideal: '', time: 2 }] });
  };

  return (
    <div>
      <div className="space-y-4">
        {questions.map((q, idx) => (
          <QuestionRow
            key={q.id}
            question={q}
            index={idx}
            onUpdate={(patch) => update(q.id, patch)}
            onRemove={() => remove(q.id)}
          />
        ))}
      </div>

      <button
        onClick={add}
        disabled={questions.length >= MAX_QUESTIONS}
        className="mt-5 text-[13px] font-semibold text-[#5B4FE8] hover:underline disabled:text-[#B8B3D4] disabled:no-underline inline-flex items-center gap-1"
      >
        <Plus size={13} />
        Add another (up to {Math.max(0, MAX_QUESTIONS - questions.length)} more)
      </button>
    </div>
  );
}
